<template>
  <section class="top-brand-sales" :class="{ dark: isDark }" aria-labelledby="top-brand-sales-title">
    <header class="chart-header">
      <h3 id="top-brand-sales-title">Top Brand Sales</h3>
    </header>

    <div v-if="loading" class="chart-state" role="status">
      <i class="material-icons spin" aria-hidden="true">refresh</i>
      Loading data...
    </div>
    <div v-else-if="error" class="chart-state chart-error" role="alert">
      Unable to load brand sales for the selected period
    </div>
    <div v-else-if="!brands.length" class="chart-state">
      No data available for the selected period
    </div>
    <div v-else class="brand-content">
      <div class="chart-wrapper">
        <EChartsHorizontalBarChart
          :categories="revenueChartCategories"
          :values="revenueChartValues"
          series-name="Revenue"
          aria-label="Top brand sales revenue ranking"
          :is-dark="isDark"
          :value-formatter="formatCurrency"
          :compact-axis-value-formatter="formatCompactCurrency"
        />
      </div>

      <div class="table-container">
        <table class="brands-table">
          <thead>
            <tr>
              <th scope="col" :aria-sort="sortAria('brand')">
                <button type="button" class="sort-button" @click="sortBy('brand')">
                  Brand <SortIcon column="brand" :active="sortColumn" :direction="sortDirection" />
                </button>
              </th>
              <th class="numeric" scope="col" :aria-sort="sortAria('revenue')">
                <button type="button" class="sort-button" @click="sortBy('revenue')">
                  Revenue <SortIcon column="revenue" :active="sortColumn" :direction="sortDirection" />
                </button>
              </th>
              <th class="numeric" scope="col" :aria-sort="sortAria('units')">
                <button type="button" class="sort-button" @click="sortBy('units')">
                  Units <SortIcon column="units" :active="sortColumn" :direction="sortDirection" />
                </button>
              </th>
              <th class="numeric" scope="col" :aria-sort="sortAria('average_price')">
                <button type="button" class="sort-button" @click="sortBy('average_price')">
                  Avg. Price <SortIcon column="average_price" :active="sortColumn" :direction="sortDirection" />
                </button>
              </th>
              <th class="numeric" scope="col" :aria-sort="sortAria('order_count')">
                <button type="button" class="sort-button" @click="sortBy('order_count')">
                  Orders <SortIcon column="order_count" :active="sortColumn" :direction="sortDirection" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="brandRow in sortedBrands" :key="brandRow.brand">
              <td class="brand-name">{{ brandRow.brand }}</td>
              <td class="numeric amount">{{ formatCurrency(brandRow.revenue) }}</td>
              <td class="numeric">{{ formatNumber(brandRow.units) }}</td>
              <td class="numeric amount">{{ formatCurrency(brandRow.average_price) }}</td>
              <td class="numeric">{{ formatNumber(brandRow.order_count, 0) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import SalesFetchApi from '../../utils/fetch/sales-fetch-api';
import EChartsHorizontalBarChart from './EChartsHorizontalBarChart.vue';
import { getBusinessDateRangeForPreset } from '../../utils/business-date';

const SortIcon = {
  functional: true,
  props: ['column', 'active', 'direction'],
  render(h, { props }) {
    if (props.column !== props.active) return null;
    return h('i', { class: ['material-icons', 'sort-icon'] }, props.direction === 'asc' ? 'arrow_upward' : 'arrow_downward');
  }
};

export default {
  name: 'TopBrandSalesChart',
  components: { EChartsHorizontalBarChart, SortIcon },
  props: {
    isDark: { type: Boolean, default: false },
    initialDateRange: { type: String, default: 'last30days' },
    customDateRange: {
      type: Object,
      default: () => ({ start: '', end: '' })
    },
    includePilotSales: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      error: false,
      brands: [],
      selectedRange: this.initialDateRange,
      startDate: '',
      endDate: '',
      limit: 20,
      sortColumn: 'revenue',
      sortDirection: 'desc',
      pendingRequestKey: '',
      fetchRequestId: 0
    };
  },
  computed: {
    revenueChartRows() {
      return [...this.brands]
        .sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0) || String(a.brand).localeCompare(String(b.brand)))
        .slice(0, 6);
    },
    revenueChartCategories() {
      return this.revenueChartRows.map(row => row.brand || 'Unbranded');
    },
    revenueChartValues() {
      return this.revenueChartRows.map(row => Math.round(Number(row.revenue || 0) * 100) / 100);
    },
    sortedBrands() {
      return [...this.brands].sort((a, b) => {
        const left = a[this.sortColumn];
        const right = b[this.sortColumn];
        if (this.sortColumn === 'brand') {
          const comparison = String(left || '').localeCompare(String(right || ''));
          return this.sortDirection === 'asc' ? comparison : -comparison;
        }

        const comparison = Number(left || 0) - Number(right || 0);
        if (comparison !== 0) {
          return this.sortDirection === 'asc' ? comparison : -comparison;
        }
        return String(a.brand || '').localeCompare(String(b.brand || ''));
      }).slice(0, 10);
    }
  },
  created() {
    this.updateDateRange();
  },
  watch: {
    initialDateRange(nextRange) {
      if (nextRange !== this.selectedRange) {
        this.selectedRange = nextRange;
        this.updateDateRange();
      }
    },
    customDateRange: {
      deep: true,
      handler(nextRange) {
        if (this.selectedRange === 'custom' &&
            (nextRange.start !== this.startDate || nextRange.end !== this.endDate)) {
          this.startDate = nextRange.start;
          this.endDate = nextRange.end;
          this.fetchData();
        }
      }
    },
    includePilotSales() {
      this.fetchData();
    }
  },
  methods: {
    formatNumber(value, maximumFractionDigits = 2) {
      return (Number(value) || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits
      });
    },
    formatCurrency(value) {
      return `$${(Number(value) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatCompactCurrency(value) {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(Number(value) || 0);
    },
    sortAria(column) {
      if (column !== this.sortColumn) return 'none';
      return this.sortDirection === 'asc' ? 'ascending' : 'descending';
    },
    updateDateRange() {
      if (this.selectedRange === 'custom') {
        if (this.customDateRange.start && this.customDateRange.end) {
          this.startDate = this.customDateRange.start;
          this.endDate = this.customDateRange.end;
        }
      } else {
        const range = getBusinessDateRangeForPreset(this.selectedRange);
        this.startDate = range.start;
        this.endDate = range.end;
      }
      this.fetchData();
    },
    setDateRange(range, customDates = null) {
      this.selectedRange = range;
      if (customDates && customDates.start && customDates.end) {
        this.startDate = customDates.start;
        this.endDate = customDates.end;
        this.fetchData();
      } else {
        this.updateDateRange();
      }
    },
    async fetchData() {
      if (!this.startDate || !this.endDate) return;
      const requestKey = [this.startDate, this.endDate, this.limit, this.includePilotSales].join('|');
      if (requestKey === this.pendingRequestKey) return;
      const requestId = ++this.fetchRequestId;
      this.pendingRequestKey = requestKey;
      this.loading = true;
      this.error = false;
      try {
        const response = await SalesFetchApi.getSalesByBrand({
          startDate: this.startDate,
          endDate: this.endDate,
          limit: this.limit,
          includePilotSales: this.includePilotSales
        });
        if (requestId !== this.fetchRequestId) return;
        this.brands = response && Array.isArray(response.result) ? response.result : [];
      } catch (error) {
        if (requestId !== this.fetchRequestId) return;
        console.error('Error fetching sales by brand data:', error);
        this.brands = [];
        this.error = true;
      } finally {
        if (requestId === this.fetchRequestId) {
          this.pendingRequestKey = '';
          this.loading = false;
        }
      }
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = column === 'brand' ? 'asc' : 'desc';
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.top-brand-sales {
  min-width: 0;
  padding: $cardPaddingY $cardPaddingX;
  background: $lightCardBg;
  border: 1px solid $lightBorderColor;
  border-radius: 8px;
  box-shadow: $cardShadow;
  color: $textColor;
}
.top-brand-sales:hover { box-shadow: $cardShadowHover; }
.top-brand-sales.dark { background: $darkCardBg; border-color: $darkBorderColor; color: $darkTextColor; }
.chart-header { margin-bottom: 12px; }
.chart-header h3 { margin: 0; color: $primaryColor; font-size: 16px; font-weight: 600; }
.dark .chart-header h3 { color: $secondaryColorDark; }
.chart-state { padding: 36px 12px; text-align: center; opacity: 0.72; }
.chart-error { color: $dangerColor; opacity: 1; }
.brand-content { display: flex; flex-direction: column; gap: 12px; }
.chart-wrapper { height: 190px; min-height: 190px; padding-bottom: 10px; border-bottom: 1px solid $lightBorderColor; }
.dark .chart-wrapper { border-color: $darkBorderColor; }
.table-container { overflow: auto; max-height: 220px; }
.brands-table { width: 100%; min-width: 520px; border-collapse: collapse; font-size: 13px; }
.brands-table th, .brands-table td { padding: $tableCellPaddingY $tableCellPaddingX; border-bottom: 1px solid $lightBorderColor; }
.brands-table th { position: sticky; top: 0; z-index: 1; background: $lightCardBg; color: $textColor; font-size: $tableHeaderFontSize; font-weight: 600; text-align: left; white-space: nowrap; }
.brands-table .numeric { text-align: right; }
.brands-table .amount { color: $successColor; font-weight: 500; }
.brand-name { font-weight: 500; }
.sort-button { width: 100%; padding: 0; border: 0; background: transparent; color: inherit; font: inherit; font-weight: inherit; text-align: inherit; cursor: pointer; }
.sort-button:focus-visible { outline: 2px solid $secondaryColor; outline-offset: 3px; }
.dark .brands-table th { background: $darkCardBg; color: $darkTextColor; }
.dark .brands-table th, .dark .brands-table td { border-color: $darkBorderColor; }
.dark .brands-table .amount { color: $successColorDark; }
.sort-icon { margin-left: 3px; font-size: 12px; vertical-align: middle; }
.spin { animation: spin 1s linear infinite; vertical-align: middle; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 600px) {
  .top-brand-sales { padding: 12px; }
  .chart-wrapper { height: 170px; min-height: 170px; }
  .brands-table { font-size: 12px; }
}
</style>
